import { ApiEnvelope } from './migrations';
import { getJson, postJsonWithStatus } from './client';

// FD-283 — client for GET /v1/devices (FD-277 / FD-ADR-031 §5).
//
// The cross-migration device index. It answers exactly one question — "which
// campaign is this serial in?" — and it deliberately answers no others.

/** The minimum search length. Below this the endpoint is not called at all. */
export const MIN_DEVICE_SEARCH_LENGTH = 3;

export interface DeviceIndexEntry {
  serialNumber: string;
  migrationId: string;
  /**
   * When the DeviceIndex row was last written — i.e. when the device was
   * assigned or re-assigned.
   *
   * NOT device activity, and named so it cannot be mistaken for it. There is
   * deliberately **no `status`** on this shape: `DeviceIndex.Status` is written
   * once at assignment as the literal `"pending"` and nothing ever updates it,
   * so surfacing it would report every device as pending forever (FD-277; the
   * FD-269 defect class). Live status lives on the campaign's device grid and
   * on the device detail page.
   */
  assignedAt: string | null;
}

export interface DevicesListResponse {
  devices: DeviceIndexEntry[];
  continuationToken: string | null;
}

export interface GetDevicesParams {
  /** Serial PREFIX. The server uppercases; a lowercase query still matches. */
  q?: string;
  pageSize?: number;
  continuationToken?: string;
}

// FD-313 (FD-ADR-033) — the audited recovery-password reveal. POST, never GET,
// so the value can never reach a URL; `reason` is required (the reveal is
// audited with who/when/why). A 404 is deliberately uninformative: unknown,
// never-salvaged, and already-closed serials are indistinguishable (no
// existence oracle — "this serial was salvaged" is itself information).
export interface RecoveryRevealResult {
  serialNumber: string;
  jobId: string;
  migrationId: string;
  salvagedUtc: string;
  failedActionId: string;
  recoveryPassword: string;
  revealedBy: string;
  revealedUtc: string;
}

export async function revealRecoveryPassword(
  serialNumber: string,
  reason: string,
): Promise<RecoveryRevealResult> {
  const { body } = await postJsonWithStatus<ApiEnvelope<RecoveryRevealResult>>(
    `/v1/devices/${encodeURIComponent(serialNumber)}/recovery-password`,
    { reason },
  );
  return body.data;
}

export async function getDevices(params: GetDevicesParams = {}): Promise<DevicesListResponse> {
  const qs = new URLSearchParams();
  if (params.q) qs.set('q', params.q);
  if (params.pageSize) qs.set('pageSize', String(params.pageSize));
  if (params.continuationToken) qs.set('continuationToken', params.continuationToken);

  const query = qs.toString();
  const envelope = await getJson<ApiEnvelope<DevicesListResponse>>(
    `/v1/devices${query ? `?${query}` : ''}`,
  );

  return {
    devices: envelope.data?.devices ?? [],
    // Normalised to null: the server sends null when there is no next page, but
    // an absent key must read the same way or the caller pages forever.
    continuationToken: envelope.data?.continuationToken ?? null,
  };
}
